import {ReactElement, useState} from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Tray} from 'interfaces/Project';
import ShowTray from './ShowTray';
import TrayCard from './TrayCard';

type Props = {
  trays: Tray[];
  onSelectTray: (tray: Tray) => void;
};

const foundTraysHeadingSx = {fontSize: 20, fontWeight: 500, lineHeight: 1.5, color: 'ctaDark'};
const blockHeadingSx = {fontSize: 18, fontWeight: 500, lineHeight: 1.5, color: 'ctaDark'};

const gridSx = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
  gap: 1.5
};

export default function FoundTrays({trays, onSelectTray}: Props): ReactElement {
  const {t} = useTranslation('common');
  const flangesNames = Array.from(new Set(trays.map((tray) => `${tray.flange.name}`))).sort(
    (a, b) => parseFloat(a) - parseFloat(b)
  );
  const [selectedFlange, setSelectedFlange] = useState(flangesNames[0] ?? '');
  const [detailsTray, setDetailsTray] = useState<Tray | null>(null);

  const filteredTrays = trays.filter((tray) => `${tray.flange.name}` === selectedFlange);
  const [recommendedTray, ...otherTrays] = filteredTrays;

  function handleFlangeChange(event: SelectChangeEvent) {
    setSelectedFlange(event.target.value);
  }

  function confirmDetails() {
    if (detailsTray) {
      onSelectTray(detailsTray);
      setDetailsTray(null);
    }
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <Typography sx={foundTraysHeadingSx}>{t('common:foundTrays')}</Typography>

      <FormControl fullWidth>
        <InputLabel id="flange-select-label">{t('common:selectFlange')}</InputLabel>
        <Select
          labelId="flange-select-label"
          id="flange-select"
          value={selectedFlange}
          label={t('common:selectFlange')}
          onChange={handleFlangeChange}
        >
          {flangesNames.map((flangeName) => (
            <MenuItem value={`${flangeName}`} key={`flange--${flangeName}`}>
              {flangeName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {recommendedTray && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
          <Typography sx={blockHeadingSx}>{t('common:recommendedTray')}</Typography>
          <Box sx={gridSx}>
            <TrayCard
              tray={recommendedTray}
              onSelect={() => onSelectTray(recommendedTray)}
              onDetails={() => setDetailsTray(recommendedTray)}
              t={t}
            />
          </Box>
        </Box>
      )}

      {otherTrays.length > 0 && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
          <Typography sx={blockHeadingSx}>{t('common:otherOptions')}</Typography>
          <Box sx={gridSx}>
            {otherTrays.map((tray) => (
              <TrayCard
                key={tray.id}
                tray={tray}
                onSelect={() => onSelectTray(tray)}
                onDetails={() => setDetailsTray(tray)}
                t={t}
              />
            ))}
          </Box>
        </Box>
      )}

      {detailsTray && (
        <ShowTray
          tray={detailsTray}
          onCancel={() => setDetailsTray(null)}
          onConfirm={confirmDetails}
        />
      )}
    </Box>
  );
}
